import { WIconComponentProps } from '@alice-health/wonderland'
import { WIcon } from '@alice-health/wonderland-react'
import classNames from 'classnames'
import Image from 'next/image'

interface MarkerBaseProps {
  image?: string
  icon?: WIconComponentProps['icon']
  iconColor?: string
  backgroundColor?: string
}

interface MarkerWithIconProps extends MarkerBaseProps {
  image?: undefined
  icon: WIconComponentProps['icon']
  iconColor?: string
  backgroundColor?: string
}

interface MarkerWithImageProps extends MarkerBaseProps {
  image: string
  icon?: undefined
  iconColor?: undefined
  backgroundColor?: undefined
}

type MarkerProps = MarkerWithIconProps | MarkerWithImageProps

/**
 * Custom Marker
 * @param icon - Name of the icon to be displayed
 * @param iconColor - Color of the icon. Use tailwind tokens, the default value is 'text-sys-color-content-on-brand'.
 * @param backgroundColor - Background color of the marker. Use tailwind tokens, the default value is 'gl-color-shades-magenta-60'.
 * */
const Marker = ({
  image,
  icon,
  iconColor = 'text-sys-color-content-on-brand',
  backgroundColor = 'bg-gl-color-shades-magenta-60'
}: MarkerProps) => {
  return (
    <div
      data-testid='marker'
      className='group w-fit h-fit cursor-pointer flex flex-col gap-[2px] items-center'
    >
      {image ? (
        <div className='h-[32px] w-[32px] p-gl-spacing-01 bg-sys-color-surface-container-active rounded-gl-border-radius-xs border-gl-border-width-md border-gl-color-shades-gray-50 group-hover:border-sys-color-stroke-hover shadow-gl-shadow-level-01 flex justify-center items-center'>
          {/*
           * O handoff recorta a logo do prestador num circulo de 24px com a
           * imagem de 20px dentro, e nao na caixa inteira do pin.
           */}
          <div className='h-[24px] w-[24px] rounded-gl-border-radius-pill overflow-hidden flex justify-center items-center'>
            <Image
              className='h-[20px] w-[20px] object-contain'
              src={image}
              alt='Marcador representando um estabelecimento credenciado'
              height={20}
              width={20}
              priority
            />
          </div>
        </div>
      ) : (
        <div
          /*
           * O handoff nao desenha borda no pin de categoria. A borda fica
           * transparente para o hover trocar so a cor, sem mudar a geometria.
           * O tom vem do token transparente do design system: o preset
           * substitui `theme.colors`, entao a classe `border-transparent` do
           * Tailwind nao existe neste projeto.
           */
          className={`h-[32px] w-[32px] p-gl-spacing-01 ${backgroundColor} rounded-gl-border-radius-xs border-gl-border-width-md border-gl-color-transparent-white-00 group-hover:border-sys-color-stroke-hover shadow-gl-shadow-level-01 flex justify-center items-center`}
        >
          <div className={`${iconColor}`}>
            <WIcon icon={icon} />
          </div>
        </div>
      )}

      <div
        className={classNames(
          'w-1 h-1 group-hover:bg-sys-color-surface-background-dark rounded-gl-border-radius-pill',
          image ? 'bg-gl-color-shades-gray-50' : backgroundColor
        )}
      />
    </div>
  )
}

export type {
  MarkerProps,
  MarkerBaseProps,
  MarkerWithIconProps,
  MarkerWithImageProps
}

export { Marker }
